import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useStore } from '@/state/store';
import { CHART_HELP } from '@/content/chartHelp';
import { fmtCurrency, fmtCurrencyCompact, fmtPct } from '@/lib/format';
import type { ProjectionResult } from '@/types';
import { ChartCard } from './ChartCard';
import { COLORS, downsample, disp, toPoints, useIsDark, yearAxisProps, TOOLTIP_PROPS } from './chartUtils';

function useResult(): { result: ProjectionResult; showReal: boolean; inflation: number } | null {
  const result = useStore((s) => s.result);
  const showReal = useStore((s) => s.ui.showRealDollars);
  const inflation = useStore((s) => s.inputs.inflationAnnualPct);
  if (!result) return null;
  return { result, showReal, inflation };
}

function gridColor(dark: boolean) {
  return dark ? '#334155' : '#e2e8f0';
}
const axisProps = (dark: boolean) => ({ stroke: dark ? '#94a3b8' : '#64748b', fontSize: 11 });
const xAxis = (dark: boolean, data: readonly { year?: number }[]) => (
  <XAxis {...axisProps(dark)} {...yearAxisProps(data)} />
);
const currencyTip = (v: number | string) => fmtCurrency(Number(v));

export function NetWorthChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = toPoints(
    r.result.rows,
    { portfolio: (x) => x.portfolioValue, loan: (x) => x.endBalance, netWorth: (x) => x.netWorth },
    r.showReal,
    r.inflation,
    ['portfolio', 'loan', 'netWorth'],
  );
  return (
    <ChartCard title="Net worth over time" help={CHART_HELP.netWorth}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} formatter={currencyTip} labelFormatter={(y) => `Year ${y}`} />
          <Legend />
          <Line type="monotone" dataKey="portfolio" name="Portfolio" stroke={COLORS.portfolio} dot={false} />
          <Line type="monotone" dataKey="loan" name="Loan" stroke={COLORS.loan} dot={false} />
          <Line type="monotone" dataKey="netWorth" name="Net worth" stroke={COLORS.netWorth} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function BorrowVsSellChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const sellByMonth = new Map(r.result.sellPath.rows.map((row) => [row.monthIndex, row.netWorth]));
  const data = downsample(r.result.rows).map((row) => ({
    year: +(row.monthIndex / 12).toFixed(2),
    borrow: disp(row.netWorth, row.monthIndex, r.showReal, r.inflation),
    sell: disp(sellByMonth.get(row.monthIndex) ?? 0, row.monthIndex, r.showReal, r.inflation),
  }));
  return (
    <ChartCard title="Borrow vs sell — net worth" help={CHART_HELP.borrowVsSell}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} formatter={currencyTip} labelFormatter={(y) => `Year ${y}`} />
          <Legend />
          <Line type="monotone" dataKey="borrow" name="Borrow" stroke={COLORS.borrow} strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="sell" name="Sell" stroke={COLORS.sell} strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function BalanceChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = toPoints(r.result.rows, { balance: (x) => x.endBalance }, r.showReal, r.inflation, ['balance']);
  return (
    <ChartCard title="Outstanding balance" help={CHART_HELP.balance}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} formatter={currencyTip} labelFormatter={(y) => `Year ${y}`} />
          <Area type="monotone" dataKey="balance" name="Balance" stroke={COLORS.loan} fill={COLORS.loan} fillOpacity={0.2} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function MonthlyInterestChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = toPoints(r.result.rows, { interest: (x) => x.interestThisMonth }, r.showReal, r.inflation, ['interest']);
  return (
    <ChartCard title="Monthly interest" help={CHART_HELP.monthlyInterest}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} cursor={false} formatter={currencyTip} labelFormatter={(y) => `Year ${y}`} />
          <Bar dataKey="interest" name="Interest" fill={COLORS.interest} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function CumulativeInterestChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = toPoints(r.result.rows, { total: (x) => x.cumulativeInterest }, r.showReal, r.inflation, ['total']);
  return (
    <ChartCard title="Cumulative interest" help={CHART_HELP.cumulativeInterest}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} formatter={currencyTip} labelFormatter={(y) => `Year ${y}`} />
          <Area type="monotone" dataKey="total" name="Cumulative interest" stroke={COLORS.interest} fill={COLORS.interest} fillOpacity={0.2} />
        </AreaChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function AprChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = toPoints(r.result.rows, { apr: (x) => x.apr, afterTax: (x) => x.afterTaxApr }, false, 0);
  return (
    <ChartCard title="Effective APR over time" help={CHART_HELP.apr}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={(v: number) => `${v.toFixed(0)}%`} width={42} />
          <Tooltip {...TOOLTIP_PROPS} formatter={(v: number | string) => fmtPct(Number(v))} labelFormatter={(y) => `Year ${y}`} />
          <Legend />
          <Line type="monotone" dataKey="apr" name="APR" stroke={COLORS.apr} dot={false} />
          <Line type="monotone" dataKey="afterTax" name="After-tax APR" stroke={COLORS.afterTaxApr} strokeDasharray="4 2" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function LtvChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const data = downsample(r.result.rows).map((row) => ({
    year: +(row.monthIndex / 12).toFixed(2),
    ltv: Number.isFinite(row.ltv) ? +(row.ltv * 100).toFixed(2) : null,
    call: row.maintenanceCall ? +(row.ltv * 100).toFixed(2) : null,
  }));
  return (
    <ChartCard title="Loan-to-value & call cushion" help={CHART_HELP.ltv}>
      <ResponsiveContainer>
        <LineChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          {xAxis(dark, data)}
          <YAxis {...axisProps(dark)} tickFormatter={(v: number) => `${v.toFixed(0)}%`} width={42} domain={[0, 'auto']} />
          <Tooltip {...TOOLTIP_PROPS} formatter={(v: number | string) => (v == null ? 'n/a' : `${Number(v).toFixed(1)}%`)} labelFormatter={(y) => `Year ${y}`} />
          <ReferenceArea y1={90} y2={1000} fill={COLORS.danger} fillOpacity={0.08} />
          <ReferenceLine y={100} stroke={COLORS.danger} strokeDasharray="4 2" label={{ value: '100% — call', fontSize: 10, fill: COLORS.danger }} />
          <Line type="monotone" dataKey="ltv" name="LTV %" stroke={COLORS.ltv} dot={false} connectNulls />
          <Line type="monotone" dataKey="call" name="Maintenance call" stroke={COLORS.danger} dot={{ r: 3 }} legendType="none" />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}

export function EstateChart() {
  const dark = useIsDark();
  const r = useResult();
  if (!r) return null;
  const { estate } = r.result;
  const data = [
    { name: 'Borrow', value: estate.borrowHeirsValue },
    { name: 'Sell', value: estate.sellHeirsValue },
  ];
  return (
    <ChartCard
      title="Estate — value to heirs"
      help={CHART_HELP.estate}
      footer={
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Step-up income tax avoided (borrow): <strong>{fmtCurrency(estate.lifetimeIncomeTaxAvoided)}</strong>. Difference:{' '}
          <strong>{fmtCurrency(estate.difference)}</strong> ({estate.difference >= 0 ? 'borrow' : 'sell'} ahead).
        </p>
      }
    >
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={gridColor(dark)} strokeDasharray="3 3" />
          <XAxis dataKey="name" {...axisProps(dark)} />
          <YAxis {...axisProps(dark)} tickFormatter={fmtCurrencyCompact} width={56} />
          <Tooltip {...TOOLTIP_PROPS} cursor={false} formatter={currencyTip} />
          <Bar dataKey="value" name="Heirs value">
            <Cell fill={COLORS.borrow} />
            <Cell fill={COLORS.sell} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
